import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Clapperboard } from "lucide-react";
import { useAuth } from "@/app/providers/AuthProvider";
import { loginAccessToken } from "@/features/auth/api";
import { ApiError } from "@/shared/api/types";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";

/** 登录页：对接 /api/v1/login/access-token */
export function LoginPage() {
  const { isAuthenticated, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [needMfa, setNeedMfa] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const token = await loginAccessToken({
        username,
        password,
        otp_password: otp || undefined,
      });
      loginWithToken(token, remember);
      navigate(token.wizard ? "/wizard" : "/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.mfaRequired) {
        setNeedMfa(true);
        setError("需要双重验证，请输入验证码");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("登录失败，请检查后端是否已启动");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-bg p-6">
      <div className="w-full max-w-sm rounded-[var(--radius-lg)] border border-border bg-bg-elevated p-6 shadow-[0_12px_40px_oklch(0_0_0/0.35)]">
        <div className="mb-6 flex items-center gap-2.5">
          <Clapperboard className="size-7 text-brand" strokeWidth={1.75} />
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Cinema Console</h1>
            <p className="text-[12px] text-fg-subtle">深色影院控制台</p>
          </div>
        </div>

        <form className="flex flex-col gap-3" onSubmit={onSubmit}>
          <label className="flex flex-col gap-1.5 text-[12px] text-fg-muted">
            用户名
            <Input
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </label>
          <label className="flex flex-col gap-1.5 text-[12px] text-fg-muted">
            密码
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          {needMfa ? (
            <label className="flex flex-col gap-1.5 text-[12px] text-fg-muted">
              双重验证码
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />
            </label>
          ) : null}

          <label className="flex items-center gap-2 text-[12px] text-fg-muted">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="accent-[var(--color-brand)]"
            />
            记住登录
          </label>

          {error ? (
            <p className="rounded-[var(--radius-sm)] border border-danger/30 bg-danger/10 px-3 py-2 text-[12px] text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" className="mt-1 w-full" disabled={loading}>
            {loading ? "登录中…" : "进入控制台"}
          </Button>
        </form>
      </div>
    </div>
  );
}
